// Core
import React, { useCallback, useState } from 'react';

// Libraries
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../hooks/useBreakpoint';

// Types
import { Theme } from '../../types/theme';
import { CardSubItem } from '../../layout/elements/CardList/CardList.types';
import {
  MaintenanceCardItem,
  MaintenanceCardListColumnLabels,
  MaintenanceCardListProps,
  MaintenanceCardSection,
} from './MaintenanceCardList.types';

// Components
import { CaretDown } from '../../layout/icons';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../layout/elements/Paragraph';

// Styles
import {
  Container,
  CardContainer,
  CardContent,
  CardDetails,
  CardHeader,
  CardTitleContainer,
  CaretDownContainer,
  CardContentWrapper,
  CardContentInner,
  SectionContainer,
  SectionSeparator,
  ColumnLabelsRow,
  SubItemRow,
  SubItemLabel,
  SubItemValue,
  SubItemSecondaryFixedValue,
  SubItemFixedValue,
} from './MaintenanceCardList.style';

const renderColumnLabels = (
  columnLabels: MaintenanceCardListColumnLabels,
  titleTextColor: string,
  labelSize: ParagraphSize,
): JSX.Element => (
  <ColumnLabelsRow>
    <SubItemLabel>
      <Paragraph
        size={labelSize}
        color={titleTextColor}
      >
        {columnLabels.left}
      </Paragraph>
    </SubItemLabel>
    {columnLabels.center && (
      <SubItemSecondaryFixedValue>
        <Paragraph
          size={labelSize}
          color={titleTextColor}
        >
          {columnLabels.center}
        </Paragraph>
      </SubItemSecondaryFixedValue>
    )}
    {columnLabels.center ? (
      <SubItemFixedValue>
        <Paragraph
          size={labelSize}
          color={titleTextColor}
        >
          {columnLabels.right}
        </Paragraph>
      </SubItemFixedValue>
    ) : (
      <SubItemValue>
        <Paragraph
          size={labelSize}
          color={titleTextColor}
        >
          {columnLabels.right}
        </Paragraph>
      </SubItemValue>
    )}
  </ColumnLabelsRow>
);

const renderSubItems = (
  subItems: CardSubItem[],
  titleTextColor: string,
  size: ParagraphSize,
  columnLabels?: MaintenanceCardListColumnLabels,
): JSX.Element => (
  <CardDetails>
    {columnLabels && renderColumnLabels(columnLabels, titleTextColor, size)}
    {subItems.map((subItem: CardSubItem) => (
      <SubItemRow key={subItem.id ?? subItem.label}>
        <SubItemLabel>
          <Paragraph
            size={size}
            color={titleTextColor}
            preventOverflow
          >
            {subItem.label}
          </Paragraph>
        </SubItemLabel>
        {subItem.secondaryValue !== undefined && (
          <SubItemSecondaryFixedValue>
            <Paragraph
              size={size}
              weight={ParagraphWeight.medium}
              preventOverflow
            >
              {subItem.secondaryValue}
            </Paragraph>
          </SubItemSecondaryFixedValue>
        )}
        {subItem.secondaryValue !== undefined ? (
          <SubItemFixedValue>
            <Paragraph
              size={size}
              weight={ParagraphWeight.medium}
              preventOverflow
            >
              {subItem.value}
            </Paragraph>
          </SubItemFixedValue>
        ) : (
          <SubItemValue>
            <Paragraph
              size={size}
              weight={ParagraphWeight.medium}
              preventOverflow
            >
              {subItem.value}
            </Paragraph>
          </SubItemValue>
        )}
      </SubItemRow>
    ))}
  </CardDetails>
);

function MaintenanceCardList({
  className,
  columnLabels,
  items,
  testId,
}: MaintenanceCardListProps): JSX.Element {
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();
  const [expandedCardKey, setExpandedCardKey] = useState<string | null>(null);

  const handleToggle = useCallback((key: string): void => {
    setExpandedCardKey((prev: string | null) => (prev === key ? null : key));
  }, []);

  const { titleTextColor } = theme.color.maintenanceCardList;

  return (
    <Container className={className} data-testid={testId}>
      {items.map((item: MaintenanceCardItem) => {
        const isExpanded: boolean = expandedCardKey === item.key;
        const hasSections: boolean = !!item.sections && item.sections.length > 0;

        return (
          <CardContainer key={item.key}>
            <CardHeader
              data-testid={`${testId}-card-header-${item.key}`}
              onClick={() => handleToggle(item.key)}
            >
              <CardTitleContainer>
                <Paragraph
                  size={ParagraphSize.sm}
                  weight={ParagraphWeight.bold}
                >
                  {item.title}
                </Paragraph>
              </CardTitleContainer>
              <CaretDownContainer isExpanded={isExpanded}>
                <CaretDown />
              </CaretDownContainer>
            </CardHeader>
            <CardContentWrapper isExpanded={isExpanded}>
              <CardContentInner>
                <CardContent>
                  {hasSections ? (
                    <SectionContainer>
                      {item.sections!.map((section: MaintenanceCardSection, index: number) => (
                        <React.Fragment key={section.key}>
                          {index > 0 && <SectionSeparator />}
                          <Paragraph
                            size={
                              isXs
                                ? ParagraphSize.xs
                                : ParagraphSize.sm
                            }
                            weight={ParagraphWeight.bold}
                          >
                            {section.title}
                          </Paragraph>
                          {renderSubItems(
                            section.subItems,
                            titleTextColor,
                            isXs ? ParagraphSize.xs : ParagraphSize.sm,
                            columnLabels,
                          )}
                        </React.Fragment>
                      ))}
                    </SectionContainer>
                  ) : (
                    renderSubItems(
                      item.subItems,
                      titleTextColor,
                      isXs ? ParagraphSize.xs : ParagraphSize.sm,
                      columnLabels,
                    )
                  )}
                </CardContent>
              </CardContentInner>
            </CardContentWrapper>
          </CardContainer>
        );
      })}
    </Container>
  );
}

export { MaintenanceCardList };
